/**
 * Viewpoint jump (parent spec §9.1 mode 4 / §9.2, AC-N3): place the camera
 * at a saved viewpoint's precise BNG coordinate + eye height + bearing.
 *
 * Hard constraint #6 (ground elevations come from the DTM only): the
 * vertical placement always comes from `sampleGround` — the LOD1 TIN
 * collider, itself DTM-derived and error-budget-asserted in the pipeline
 * (R3/AC-T1) — never from a viewpoint's own elevation field. Several
 * saved viewpoints (e.g. the default) carry an
 * `observer_elevation_m_aod_user_supplied` value; that field is exactly
 * what its name says, a user-supplied annotation, and is deliberately
 * never read here.
 */
import { bngToLocal } from '../core/coordinates';
import type { Viewpoint } from '../core/manifest';
import { EYE_HEIGHT_M, type GroundSampler } from './groundLock';

export interface CameraPose {
  x: number;
  /** eye elevation, metres AOD */
  y: number;
  z: number;
  bearingDeg: number;
  fovDeg: number;
}

export class ViewpointCoordinateMissingError extends Error {
  constructor(viewpointId: string) {
    super(
      `viewpoint "${viewpointId}" has no resolved easting/northing yet ` +
        '(coordinate_status is pending) — refusing to place it without a coordinate.',
    );
    this.name = 'ViewpointCoordinateMissingError';
  }
}

/** True when a viewpoint's position is an approximate, non-surveyed estimate
 * (settlement centroid / nearest known point) rather than a published or
 * surveyed coordinate. Signalled by an `estimated_*` coordinate_status. */
export function isEstimatedViewpoint(viewpoint: Viewpoint): boolean {
  return viewpoint.coordinate_status.startsWith('estimated_');
}

export interface ViewpointMenuEntry {
  /** Text to show in the jump dropdown. */
  label: string;
  /** Whether the option is un-jumpable (no resolved coordinate). */
  disabled: boolean;
}

/**
 * Decides how a viewpoint appears in the jump menu (issue #10). Three cases:
 *  - resolved + estimated → jumpable, labelled "(approx.)" so an approximate
 *    settlement/area point is never mistaken for a surveyed one;
 *  - resolved + published/surveyed → jumpable, plain name;
 *  - unresolved (easting/northing still null) → disabled, "coordinates
 *    pending". This last path is reserved for genuinely point-precise
 *    coordinates that are still missing — not for place/area viewpoints,
 *    which issue #10 legitimately estimates.
 */
export function viewpointMenuEntry(viewpoint: Viewpoint): ViewpointMenuEntry {
  const resolved = viewpoint.easting !== null && viewpoint.northing !== null;
  if (!resolved) {
    return { label: `${viewpoint.name} — coordinates pending`, disabled: true };
  }
  if (isEstimatedViewpoint(viewpoint)) {
    return { label: `${viewpoint.name} (approx.)`, disabled: false };
  }
  return { label: viewpoint.name, disabled: false };
}

export class ViewpointGroundMissingError extends Error {
  constructor(viewpointId: string) {
    super(`no terrain tile is loaded under viewpoint "${viewpointId}" — cannot ground-lock the camera there.`);
    this.name = 'ViewpointGroundMissingError';
  }
}

/** Bounds a viewpoint's `fov_deg` must fall strictly within to be applied
 * (issue #95). Chosen well clear of the two view-scale presets (27°/60°) so
 * any plausible custom framing is accepted; a stuck/misconfigured value near
 * a lens extreme is refused rather than silently clamped. */
const FOV_MIN_DEG = 10;
const FOV_MAX_DEG = 120;

export class ViewpointFovInvalidError extends Error {
  constructor(viewpointId: string, fovDeg: unknown) {
    super(
      `viewpoint "${viewpointId}" has an invalid fov_deg (${JSON.stringify(fovDeg)}) — ` +
        `must be a finite number greater than ${FOV_MIN_DEG} and less than ${FOV_MAX_DEG}.`,
    );
    this.name = 'ViewpointFovInvalidError';
  }
}

function isValidFovDeg(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > FOV_MIN_DEG && value < FOV_MAX_DEG;
}

/**
 * Resolves the vertical FOV a jump should apply (issue #95): an explicit
 * `fov_deg` is validated and applied as-is; an absent one preserves
 * whatever the camera's FOV already is (the active True scale/Explore
 * preset), rather than forcing a fixed fallback. Never clamps or
 * substitutes an invalid value (hard constraint #7) — it throws instead.
 */
function resolveFovDeg(viewpoint: Viewpoint, currentFovDeg: number): number {
  if (viewpoint.fov_deg === undefined) return currentFovDeg;
  if (!isValidFovDeg(viewpoint.fov_deg)) {
    throw new ViewpointFovInvalidError(viewpoint.id, viewpoint.fov_deg);
  }
  return viewpoint.fov_deg;
}

/**
 * Resolves a saved viewpoint to a camera pose. Throws
 * ViewpointCoordinateMissingError if the viewpoint's BNG position is not
 * yet resolved, ViewpointGroundMissingError if no loaded tile covers it, or
 * ViewpointFovInvalidError if it carries an out-of-range fov_deg — all three
 * signal "do not fabricate", not "get a best guess". `currentFovDeg` is the
 * camera's live vertical FOV (the active view-scale preset), used only when
 * the viewpoint itself specifies no fov_deg.
 */
export function poseForViewpoint(viewpoint: Viewpoint, sampleGround: GroundSampler, currentFovDeg: number): CameraPose {
  if (viewpoint.easting === null || viewpoint.northing === null) {
    throw new ViewpointCoordinateMissingError(viewpoint.id);
  }
  const fovDeg = resolveFovDeg(viewpoint, currentFovDeg);
  const { x, z } = bngToLocal(viewpoint.easting, viewpoint.northing);
  const ground = sampleGround(x, z);
  if (ground === null) {
    throw new ViewpointGroundMissingError(viewpoint.id);
  }
  const eyeHeight = viewpoint.eye_height_m ?? EYE_HEIGHT_M;
  return {
    x,
    y: ground + eyeHeight,
    z,
    bearingDeg: viewpoint.bearing_deg ?? 0,
    fovDeg,
  };
}
